import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { AlertTriangle, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight typeahead hint shown BELOW a commodity-name input.
 *
 * - Debounces by 300 ms.
 * - Shows up to 3 suggestions where `value` normalizes to the same canonical
 *   form as an existing commodity name (case/whitespace/separator differences).
 * - Click a suggestion to call `onPick(name)`.
 *
 * Props:
 *   value:   current free-text value of the field
 *   onPick:  (name: string) => void   — invoked with the chosen canonical name
 */
export default function CommodityNameSuggestion({ value, onPick }) {
  const [data, setData] = useState([]);
  const [exact, setExact] = useState([]);
  const tRef = useRef(null);

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    if (!value || value.trim().length < 2) {
      setData([]);
      setExact([]);
      return;
    }
    tRef.current = setTimeout(() => {
      axios
        .get(`${API}/stores/commodity-suggestions`, { params: { q: value, limit: 5 } })
        .then((res) => {
          const all = res.data?.data || [];
          setExact(all.filter((s) => s.isExactNormalizedMatch));
          setData(all.filter((s) => !s.isExactNormalizedMatch).slice(0, 3));
        })
        .catch(() => {
          setData([]);
          setExact([]);
        });
    }, 300);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [value]);

  if (exact.length === 0 && data.length === 0) return null;

  return (
    <div className="mt-1 space-y-1" data-testid="commodity-suggestion-box">
      {exact.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded p-1.5 text-[11px] text-amber-800 flex items-start gap-1.5" data-testid="commodity-exact-match-warning">
          <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold mb-0.5">Did you mean an existing commodity?</div>
            {exact.map((s) => (
              <button
                key={s.name}
                type="button"
                onClick={() => onPick(s.name)}
                className="block text-left w-full hover:bg-amber-100 px-1 py-0.5 rounded transition group"
                data-testid={`commodity-suggestion-${s.name}`}
              >
                <span className="font-mono">"{s.name}"</span>
                <span className="text-amber-600 ml-1 opacity-70">
                  (used in {s.totalCount} record{s.totalCount === 1 ? '' : 's'})
                </span>
                <ChevronRight size={10} className="inline ml-1 opacity-0 group-hover:opacity-100" />
              </button>
            ))}
          </div>
        </div>
      )}
      {data.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded p-1.5 text-[11px] text-gray-600">
          <div className="font-medium text-gray-500 mb-0.5">Related existing names:</div>
          {data.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => onPick(s.name)}
              className="block text-left w-full hover:bg-gray-100 px-1 py-0.5 rounded transition truncate"
              title={s.name}
            >
              <span className="font-mono">"{s.name}"</span>
              <span className="text-gray-400 ml-1">×{s.totalCount}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
